import React, { useState, useEffect } from 'react';
import {
  List,
  Button,
  message,
  Form,
  Col,
  Row,
  Input,
  Checkbox,
  Alert,
  Tabs,
  Badge,
  Radio,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { StickyContainer, Sticky } from 'react-sticky';
import { PlusOutlined, FileTextOutlined } from '@ant-design/icons';
import { Link, useHistory, useLocation } from 'react-router-dom';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/client';
import {
  PackageStatus,
  PackageWhereInput,
  PackageQueryResult,
  ShippingGroup,
  Package,
  MutationDeletePackageArgs,
} from '../../../utils/types';
import Error from '../../../components/Error';
import ProgressModal from '../../../components/ProgressModal';

import './index.css';
import Pullable from '../../../components/Pullable';
import PackageCard from './PackageCard';

import { PlaceOrderState } from '../PlaceOrder';

const { TextArea } = Input;
const { TabPane } = Tabs;

const SHIPPING_GROUP_QUERY = gql`
  query ShippingGroupQuery($status: ShippingGroupStatus) {
    shippingGroups(status: $status) {
      id
      name
      type
      zipCode
      country
      originalShippingGroupId
      isPrivate
    }
  }
`;

export const PACKAGES_QUERY = gql`
  query PackagesQuery(
    $where: PackageWhereInput!
    $orderBy: PackageOrderByInput
    $skip: Int
    $count: Int
  ) {
    queryUserPackages(where: $where, orderBy: $orderBy, skip: $skip, count: $count) {
      total
      list {
        id
        trackingNumber
        shippingCompany
        items
        status
        notes
        warehouseNotes
        weight
        length
        width
        depth
        shippingGroup {
          id
          name
          type
          status
          shippingRoute {
            id
            name
            country
            speed
            shippingMethod
            shipmentType
            dimensionalFactor
            isActive
            canGroup
            canDirect
            priceRanges {
                startWeight
                unitPrice
                basePrice
                roundingWeight
            }
            description
          }
          country
          isPrivate
        }
        order {
          id
          number
          status
          country
        }
        subOrder {
          id
          number
          status
        }
      }
    }
  }
`;

const DELETE_PACKAGE_MUTATION = gql`
  mutation DeletePackage($id: ID!) {
    deletePackage(id: $id) {
      id
    }
  }
`;

type FormValues = {
  trackingNumbers?: string;
  notes?: string;
};

type PackageTypeFilter = 'ALL' | 'DIRECT' | 'GROUP';

function getWhere(
  status: Array<PackageStatus>,
  formData: FormValues,
  packageTypeFilter: PackageTypeFilter,
  additionalQuery?: PackageWhereInput,
): PackageWhereInput {
  if (!formData) {
    return additionalQuery ? additionalQuery : {};
  }

  const { trackingNumbers, notes } = formData;
  const where: PackageWhereInput = {};
  if (trackingNumbers) {
    where.trackingNumber = {
      in: trackingNumbers.split('\n').map(s => s.replace(/[\s,;；，]/g, '')),
    };
  }
  if (status && status.length > 0) {
    where.status = { in: status };
  }

  if (notes) {
    where.notes = { contains: notes };
  }

  if (packageTypeFilter === 'DIRECT') {
    where.shippingGroupId = { equals: null };
  } else if (packageTypeFilter === 'GROUP') {
    where.shippingGroupId = { not: null };
  }

  if (additionalQuery) {
    return { AND: [additionalQuery, where] };
  } else {
    return where;
  }
}

function usePackages(
  status: Array<PackageStatus>,
  formData: FormValues,
  packageTypeFilter: PackageTypeFilter,
  additionalQuery?: PackageWhereInput,
) {
  const prev = {
    status,
    formData,
    packageTypeFilter,
    additionalQuery,
  };

  const where = getWhere(status, formData, packageTypeFilter, additionalQuery);
  const whereDirectCount = getWhere(status, formData, 'DIRECT', additionalQuery);
  const whereGroupCount = getWhere(status, formData, 'GROUP', additionalQuery);

  const { loading, error, data, refetch } = useQuery<{
    queryUserPackages: PackageQueryResult;
  }>(PACKAGES_QUERY, {
    variables: { where, orderBy: { updatedAt: 'DESC' } },
    fetchPolicy: 'cache-and-network',
  });

  const {
    loading: loadingDirectCount,
    error: errorDirectCount,
    data: dataDirectCount,
    refetch: refetchDirectCount,
  } = useQuery<{
    queryUserPackages: PackageQueryResult;
  }>(PACKAGES_QUERY, {
    variables: { where: whereDirectCount, count: 0, orderBy: { updatedAt: 'DESC' } },
    fetchPolicy: 'cache-and-network',
  });

  const {
    loading: loadingGroupCount,
    error: errorGroupCount,
    data: dataGroupCount,
    refetch: refetchGroupCount,
  } = useQuery<{
    queryUserPackages: PackageQueryResult;
  }>(PACKAGES_QUERY, {
    variables: { where: whereGroupCount, count: 0, orderBy: { updatedAt: 'DESC' } },
    fetchPolicy: 'cache-and-network',
  });
  return {
    loading: loading || loadingDirectCount || loadingGroupCount,
    error: error || errorDirectCount || errorGroupCount,
    data: {
      packages: data?.queryUserPackages,
      directCount: dataDirectCount?.queryUserPackages.total,
      groupCount: dataGroupCount?.queryUserPackages.total,
    },
    refetch(
      status: Array<PackageStatus>,
      formData: FormValues,
      packageTypeFilter: PackageTypeFilter,
      additionalQuery?: PackageWhereInput,
    ) {
      const where = getWhere(status, formData, packageTypeFilter, additionalQuery);
      const whereDirectCount = getWhere(status, formData, 'DIRECT', additionalQuery);
      const whereGroupCount = getWhere(status, formData, 'GROUP', additionalQuery);

      if (
        JSON.stringify(status) === JSON.stringify(prev.status) &&
        JSON.stringify(formData) === JSON.stringify(prev.formData) &&
        additionalQuery === prev.additionalQuery
      ) {
        return refetch({ where, orderBy: { updatedAt: 'DESC' } });
      } else {
        return Promise.all([
          refetch({ where, orderBy: { updatedAt: 'DESC' } }),
          refetchDirectCount({ where: whereDirectCount, count: 1, orderBy: { updatedAt: 'DESC' } }),
          refetchGroupCount({ where: whereGroupCount, count: 1, orderBy: { updatedAt: 'DESC' } }),
        ]);
      }
    },
  };
}

const renderTabBar = (props, DefaultTabBar) => (
  <Sticky bottomOffset={80}>
    {({ style }) => <DefaultTabBar {...props} style={{ ...style }} />}
  </Sticky>
);

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

const defaultPackageStatus = [
  PackageStatus.WarehouseReceived,
  PackageStatus.OrderCreated,
  PackageStatus.WarehousePending,
  PackageStatus.Problem,
];

const getPackageStatusFilter = key => {
  let status: Array<PackageStatus>;
  switch (key) {
    case 'WAREHOUSE_PENDING':
    case 'WAREHOUSE_RECEIVED':
    case 'ORDER_CREATED':
    case 'PROBLEM':
      status = [key];
      break;
    case 'ALL':
      status = defaultPackageStatus;
      break;
    default:
      status = [];
  }
  return status;
};

const defaultTab = 'WAREHOUSE_RECEIVED';

function Packages() {
  const [form] = Form.useForm();

  const query = useURLQuery();
  const activeTabKey = query.get('tab') || defaultTab;

  const statusFilter = getPackageStatusFilter(activeTabKey);

  // const [advancedSearchOpen, setAdvancedSearchVisibility] = useState(false);
  const [selectable, setSelectable] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<Array<string>>([]);

  const [packageTypeFilter, setPackageTypeFilter] = useState<PackageTypeFilter>('ALL');

  useEffect(() => {
    setPackageTypeFilter('ALL');
    setSelectable(false);
    setSelectedPackages([]);
  }, [activeTabKey]);

  const history = useHistory();

  const { loading, error, data, refetch } = usePackages(
    statusFilter,
    form.getFieldsValue() as FormValues,
    packageTypeFilter,
  );

  const {
    refetch: refetchGroups,
    data: groupsData = { shippingGroups: [] as Array<ShippingGroup> },
  } = useQuery(SHIPPING_GROUP_QUERY, {
    variables: { status: 'OPEN' },
    fetchPolicy: 'cache-and-network',
  });

  const selectPackage = (e: CheckboxChangeEvent, packageId: string) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedPackages([...selectedPackages, packageId]);
    } else {
      setSelectedPackages(selectedPackages.filter(id => id !== packageId));
    }
  };

  const packages = data?.packages?.list;

  const selectablePackageIds = packages
    ?.filter(
      pkg => !pkg.shippingGroup?.id && !pkg?.order?.id && pkg.status === 'WAREHOUSE_RECEIVED',
    )
    ?.map(pkg => pkg.id);

  const selectAllPackages = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    if (checked && selectablePackageIds) {
      setSelectedPackages(selectablePackageIds);
    } else {
      setSelectedPackages([]);
    }
  };

  const toggleCreateOrder = () => {
    if (selectable) {
      const state: PlaceOrderState = {
        packageIds: selectedPackages,
        totalWeight: totalSelectedWeight() || 0,
      };
      history.push('/orders/create', state);
    } else {
      setSelectable(true);
    }
  };

  const cancelSelect = () => {
    setSelectable(false);
    setSelectedPackages([]);
  };

  const updatePackagesListFilter = (data: {
    formValues?: FormValues;
    packageTypeFilter?: PackageTypeFilter;
  }) => {
    if (data.packageTypeFilter) {
      setPackageTypeFilter(data.packageTypeFilter);
    }
    refetch(
      statusFilter,
      data.formValues || (form.getFieldsValue() as FormValues),
      data.packageTypeFilter || packageTypeFilter,
    );
  };

  const applyFilter = values => {
    console.log(values);
    updatePackagesListFilter({ formValues: values as FormValues });
  };

  const setPackagesFilter = (packageTypeFilter: PackageTypeFilter) => {
    updatePackagesListFilter({ packageTypeFilter });
  };

  const [deletePackage] = useMutation<{ deletePackage: { id: string }}, MutationDeletePackageArgs>(DELETE_PACKAGE_MUTATION, {
    onError: () => null,
    onCompleted: () => {
      ProgressModal.hide();
      message.success('已删除');
      updatePackagesListFilter({});
    },
  });

  const onConfirmDelete = id => {
    ProgressModal.show();
    deletePackage({ variables: { id } });
  };

  const totalSelectedWeight = () => packages
    ?.filter(p => selectedPackages.includes(p.id))
    ?.reduce((acc, current) => (current?.weight || 0) + acc, 0);

  if (error) return <Error />;

  const directPackageCount = data?.directCount;
  const groupPackageCount = data?.groupCount;

  const lastFoodGroupOriginalId = localStorage.getItem('last-food-group');
  const lastFoodGroup =
    lastFoodGroupOriginalId &&
    groupsData.shippingGroups.find(
      g =>
        g.originalShippingGroupId === lastFoodGroupOriginalId || g.id === lastFoodGroupOriginalId,
    );
  const lastRegularGroupOriginalId = localStorage.getItem('last-regular-group');
  const lastRegularGroup =
    lastRegularGroupOriginalId &&
    groupsData.shippingGroups.find(
      g =>
        g.originalShippingGroupId === lastRegularGroupOriginalId ||
        g.id === lastRegularGroupOriginalId,
    );

  const packageList = (
    <div className="Packages-list">
      {(activeTabKey === 'ALL' ||
        activeTabKey === 'PROBLEM' ||
        activeTabKey === 'WAREHOUSE_RECEIVED') && (
        <Alert
          style={{ marginBottom: '10px', marginTop: '-13px' }}
          type="warning"
          message={
            <div>
              直邮和拼邮的区别？不是我的包裹？更多 <Link to="/faq">常见问题</Link>
            </div>
          }
        />
      )}

      {activeTabKey !== 'PROBLEM' && activeTabKey !== 'ORDER_CREATED' && (
        <Row justify="space-between" gutter={20}>
          {!selectable && (
            <Col span={activeTabKey === 'WAREHOUSE_PENDING' ? 24 : 12}>
              <Link
                to={{
                  pathname: '/packages/add',
                  search: `?type=${packages?.[0]?.shippingGroup ? 'GROUP' : 'DIRECT'}`,
                }}
              >
                <Button
                  block
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ marginBottom: '10px', marginTop: '-1rem' }}
                >
                  预报包裹
                </Button>
              </Link>
            </Col>
          )}
          {(activeTabKey === 'ALL' || activeTabKey === 'WAREHOUSE_RECEIVED') && (
            <Col span={selectable ? 16 : 12}>
              <Button
                disabled={
                  (selectedPackages.length === 0 && selectable) || packageTypeFilter === 'GROUP'
                }
                block
                danger={!selectable}
                type={selectable ? 'primary' : 'default'}
                icon={!selectable && <FileTextOutlined />}
                style={{ marginBottom: '10px', marginTop: '-1rem' }}
                onClick={toggleCreateOrder}
              >
                {!selectable
                  ? '直邮申请打包'
                  : selectedPackages.length === 0
                  ? '请勾选直邮包裹'
                  : `共${selectedPackages.length}个包裹，创建订单`}
              </Button>
            </Col>
          )}

          {selectable && (
            <Col span={8}>
              <Button
                block
                style={{ marginBottom: '10px', marginTop: '-1rem' }}
                onClick={cancelSelect}
              >
                取消
              </Button>
            </Col>
          )}
        </Row>
      )}

      <Form form={form} onFinish={applyFilter} style={{ marginBottom: '-20px' }}>
        <Row gutter={3} justify="space-between">
          <Col span={10}>
            <Form.Item name="trackingNumbers">
              <TextArea placeholder="单号(一行一个)" autoSize rows={1} allowClear />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item name="notes">
              <Input placeholder="备注" allowClear />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item>
              <Button size="small" type="primary" htmlType="submit">
                查询
              </Button>
              {/* <Button style={{ marginLeft: '20px' }} onClick={() => setAdvancedSearchVisibility(!advancedSearchOpen)}>
                高级查询
                {advancedSearchOpen ? <UpOutlined /> : <DownOutlined />}
              </Button> */}
            </Form.Item>
          </Col>
        </Row>
        {/* <Collapse isOpened={advancedSearchOpen}>
          <div className="advancedSearchWrapper">
            <Row>
              <Col span={24} style={{ textAlign: 'right' }}>
                <Button type="primary" htmlType="submit">
                  查询
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                  重置
                </Button>
              </Col>
            </Row>
          </div>
        </Collapse> */}
      </Form>

      {(lastFoodGroup || lastRegularGroup) &&
        !selectable &&
        activeTabKey !== 'PROBLEM' &&
        activeTabKey !== 'ORDER_CREATED' && (
          <Row style={{ marginBottom: '5px' }} align="middle" justify="space-between">
            {lastRegularGroup && (
              <Col className="Packages-addGroupButton">
                <Link
                  to={`/packages/add?groupId=${lastRegularGroupOriginalId}&type=GROUP&groupType=${lastRegularGroup.type}`}
                >
                  {`添加包裹到\n${lastRegularGroup.name}`}
                </Link>
              </Col>
            )}

            {lastFoodGroup && (
              <Col className="Packages-addGroupButton">
                <Link
                  to={`/packages/add?groupId=${lastFoodGroupOriginalId}&type=GROUP&groupType=${lastFoodGroup.type}`}
                >
                  {`添加包裹到\n${lastFoodGroup.name}`}
                </Link>
              </Col>
            )}
          </Row>
        )}
      <Row style={{ marginBottom: '5px' }} align="middle">
        <Radio.Group
          defaultValue="ALL"
          onChange={e => {
            setPackagesFilter(e.target.value as PackageTypeFilter);
            setSelectable(false);
            setSelectedPackages([]);
          }}
          size="small"
          value={packageTypeFilter}
        >
          <Radio.Button value="ALL">全部</Radio.Button>
          <Radio.Button value="DIRECT">
            直邮&nbsp;
            <Badge
              showZero
              overflowCount={9999}
              count={directPackageCount}
              style={{ backgroundColor: '#1890ff' }}
              size="small"
            />
          </Radio.Button>

          <Radio.Button value="GROUP">
            拼邮&nbsp;
            <Badge
              showZero
              overflowCount={9999}
              count={groupPackageCount}
              style={{ backgroundColor: '#52c41a' }}
              size="small"
            />
          </Radio.Button>
        </Radio.Group>
      </Row>

      {selectable && packageTypeFilter !== 'GROUP' && (
        <Checkbox
          onChange={e => selectAllPackages(e)}
          checked={
            selectedPackages?.length === selectablePackageIds?.length &&
            selectedPackages?.length !== 0
          }
        >
          全选已入库直邮包裹(已选{selectedPackages?.length}个，共{totalSelectedWeight()?.toFixed(2)}kg)
        </Checkbox>
      )}

      <Pullable
        onRefresh={() => {
          updatePackagesListFilter({});
          refetchGroups();
        }}
      >
        <List
          loading={loading}
          grid={{ gutter: 16, column: 1 }}
          dataSource={packages}
          rowKey={(pkg: Package) => pkg.id}
          renderItem={(pkg: Package) => (
            <List.Item>
              <Row align="middle">
                {selectable && (
                  <Col span={2}>
                    {selectablePackageIds?.includes(pkg.id) && (
                      <Checkbox
                        onChange={e => selectPackage(e, pkg.id)}
                        checked={selectedPackages.includes(pkg.id)}
                      />
                    )}
                  </Col>
                )}
                <Col span={selectable ? 22 : 24}>
                  <PackageCard pkg={pkg} onConfirmDelete={onConfirmDelete} />
                </Col>
              </Row>
            </List.Item>
          )}
        />
      </Pullable>
    </div>
  );

  return (
    <div className="Packages">
      <StickyContainer>
        <Tabs
          hideAdd
          centered
          onChange={key => {
            history.push(`/packages?tab=${key}`);
          }}
          activeKey={activeTabKey}
          renderTabBar={renderTabBar}
        >
          <TabPane tab="全部" key="ALL">
            {packageList}
          </TabPane>
          <TabPane tab="待入库" key="WAREHOUSE_PENDING">
            {packageList}
          </TabPane>
          <TabPane tab="已入库" key="WAREHOUSE_RECEIVED">
            {packageList}
          </TabPane>
          <TabPane tab="已下单" key="ORDER_CREATED">
            {packageList}
          </TabPane>
          <TabPane tab="问题件" key="PROBLEM">
            {packageList}
          </TabPane>
        </Tabs>
      </StickyContainer>
    </div>
  );
}

export default Packages;
